import { httpServiceClient, queryClient } from '@/infra/http'

import {
  formatDependentRequestList,
  formatDependentRequestSettings
} from './dependent-service-utils'
import { makeApiUrl } from '@/main/factories/http/api-url-factory'
import { EnrollmentStatusTab } from '@/domain/models/enrollment/status'
import { formatCustomDateString } from '@/main/helpers'
import {
  DependentCreateRequest,
  DependentCreateResponse,
  DependentEnrollmentQuery,
  DependentEnrollmentResponse,
  DependentPendingRequest,
  DependentSettingsResponse
} from './dependent-service-types'

const getDependentRequestUrl = (companyId: string) =>
  makeApiUrl(`/customer/v1/company/${companyId}`)

export const dependentRequestService = {
  /** Exemplo de uso do React Query para buscar configurações, caso necessário */
  async fetchQuerySettings(companyId: string) {
    const response = await queryClient.fetchQuery({
      queryKey: ['dependent-settings', companyId],
      queryFn: () => dependentRequestService.fetchSettings(companyId),
      staleTime: 1000
    })

    return response
  },

  async fetchSettings(companyId: string) {
    const pathUrl = `${getDependentRequestUrl(companyId)}/user-request-settings`

    const response = await httpServiceClient<DependentSettingsResponse>({
      method: 'get',
      url: pathUrl
    })

    return formatDependentRequestSettings(response.body.data as DependentSettingsResponse)
  },

  async fetchPendingRequests(companyId: string) {
    const pathUrl = `${getDependentRequestUrl(companyId)}/dependent-request/pending`

    const response = await httpServiceClient<DependentPendingRequest>({
      method: 'get',
      url: pathUrl
    })

    return response.body.data as DependentPendingRequest
  },

  async createRequest(companyId: string, payload: DependentCreateRequest) {
    const pathUrl = `${getDependentRequestUrl(companyId)}/dependent-request`

    const response = await httpServiceClient<DependentCreateResponse>({
      method: 'post',
      url: pathUrl,
      body: {
        ...payload,
        birthDate: formatCustomDateString(payload.birthDate, 'yyyy-mm-dd')
      }
    })

    return response.body.data as DependentCreateResponse
  },

  async cancelRequest(companyId: string, requestUuid: string) {
    const pathUrl = `${getDependentRequestUrl(companyId)}/dependent-request/${requestUuid}/cancel`

    await httpServiceClient<void>({
      method: 'put',
      url: pathUrl
    })
  },

  async fetchRequestList(companyId: string, payload: DependentEnrollmentQuery) {
    const pathUrl = `${getDependentRequestUrl(companyId)}/dependent-request`

    const response = await httpServiceClient<DependentEnrollmentResponse>({
      method: 'get',
      url: pathUrl,
      params: {
        page: String(payload.page ?? 1),
        pageSize: String(payload.pageSize ?? 10),
        status: payload.status || EnrollmentStatusTab.WAITING_HR,
        search: payload.search || undefined
      }
    })

    return formatDependentRequestList(response.body.data as DependentEnrollmentResponse)
  },

  async approveRequest(companyId: string, requestUuid: string, chargeType: string) {
    const pathUrl = `${getDependentRequestUrl(companyId)}/dependent-request/${requestUuid}/approve`

    await httpServiceClient<void>({
      method: 'put',
      url: pathUrl,
      body: {
        charge_type: chargeType
      }
    })
  },

  async rejectRequest(companyId: string, requestUuid: string, reason: string) {
    const pathUrl = `${getDependentRequestUrl(companyId)}/dependent-request/${requestUuid}/reject`

    await httpServiceClient<void>({
      method: 'put',
      url: pathUrl,
      body: { reason }
    })
  }
}
